import {AlertTriangle, ArrowLeft, Home} from 'lucide-react';
import {Button} from './button';
import {Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent} from './empty';
import {Alert, AlertDescription, AlertTitle} from './alert';

interface ErrorPageProps {
  title?: string;
  description?: string;
  error?: Error | null;
  statusCode?: number | null;
}

export function getStatusCodeFromError(error: unknown): number | null {
  if (error instanceof Error) {
    const message = error.message;
    // Match patterns like "API Error: DetailedError 404" or "404"
    const match = message.match(/(\d{3})/);
    if (match && match[1]) {
      const code = parseInt(match[1], 10);
      if (code >= 400 && code < 600) {
        return code;
      }
    }
  }
  return null;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}

export function ErrorPage({title, description, error, statusCode: propStatusCode}: ErrorPageProps) {
  const detectedStatusCode = error ? getStatusCodeFromError(error) : null;
  const statusCode = propStatusCode ?? detectedStatusCode;
  const errorMessage = error ? getErrorMessage(error) : (description ?? '');

  const displayTitle = title ?? (statusCode ? `Error ${statusCode}` : 'Something went wrong');
  const displayDescription =
    description ??
    (statusCode === 404
      ? 'The page you are looking for does not exist.'
      : 'An unexpected error occurred. Please try again later.');

  return (
    <div className="flex h-screen w-full items-center justify-center p-6">
      <Empty className="max-w-lg">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <AlertTriangle className="size-4" />
          </EmptyMedia>
          <EmptyTitle>{displayTitle}</EmptyTitle>
          <EmptyDescription>{displayDescription}</EmptyDescription>
        </EmptyHeader>

        <EmptyContent>
          {errorMessage && (
            <Alert variant="destructive" className="w-full">
              <AlertTitle>Error Details</AlertTitle>
              <AlertDescription className="font-mono text-xs break-all">
                {errorMessage}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => window.history.back()}>
              <ArrowLeft className="mr-2 size-4" />
              Go back
            </Button>
            <Button asChild>
              <a href="/">
                <Home className="mr-2 size-4" />
                Back home
              </a>
            </Button>
          </div>
        </EmptyContent>
      </Empty>
    </div>
  );
}
